import { countryName } from '@/lib/countries';

// Value lists and display helpers for contact fields, shared by the contacts page and the campaign
// audience filters.

// Set by campaign results only, never on the form — see LEAD_STATUSES in backend/src/models/Contact.js,
// which this mirrors. A contact no campaign has reached yet has no status (null).
export type LeadStatus = 'contacted' | 'replied' | 'interested' | 'not_interested';

export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  contacted: 'Contacted',
  replied: 'Replied',
  interested: 'Interested',
  not_interested: 'Not interested',
};

// What a person may pick by hand, and only once the contact has replied. Mirrors MANUAL_LEAD_STATUSES.
export const MANUAL_LEAD_STATUSES: LeadStatus[] = ['replied', 'interested', 'not_interested'];

// The filter value for a null status. Mirrors LEAD_STATUS_NONE.
export const LEAD_STATUS_NONE = 'none';
export const NOT_CONTACTED_LABEL = 'Not contacted';

export const leadStatusLabel = (status: LeadStatus | null | undefined): string =>
  (status ? LEAD_STATUS_LABELS[status] ?? status : NOT_CONTACTED_LABEL);

export type LeadCampaign = 'current' | 'old';

// Mirrors LEAD_CAMPAIGNS in backend/src/config/contactFields.js.
export const LEAD_CAMPAIGN_LABELS: Record<LeadCampaign, string> = {
  current: 'Current Campaign',
  old: 'Old Campaign',
};

// Mirrors BUDGET_CURRENCIES in backend/src/config/contactFields.js.
export const BUDGET_CURRENCIES = ['AED', 'SAR', 'EGP', 'USD'] as const;
export type BudgetCurrency = (typeof BUDGET_CURRENCIES)[number];

/**
 * "1500000" + "AED" -> "1,500,000 AED". Grouped only when the budget is a plain number: it is free
 * text, so "1.5M" and "2-3 million" are shown as typed. Mirrors formatBudget in
 * backend/src/services/contactTemplateValues.js, which produces the text a customer receives.
 */
export const formatBudget = (budget: string | null | undefined, currency?: string | null): string => {
  const raw = String(budget ?? '').trim();
  if (!raw) return '';

  let amount = raw;
  if (/^[\d,\s]+(\.\d+)?$/.test(raw)) {
    const value = Number(raw.replace(/[,\s]/g, ''));
    if (Number.isFinite(value)) amount = value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }
  return currency ? `${amount} ${currency}` : amount;
};

/**
 * The value a template variable mapped to `field` takes for this recipient — the browser half of
 * backend/src/services/contactTemplateValues.js. Two fields are derived rather than stored: the
 * country is sent as its NAME in the template's language, and budgetWithCurrency is the budget
 * with its currency. Everything else is read straight off the recipient.
 */
export const templateValueFor = (
  recipient: Record<string, unknown> | null | undefined,
  field: string,
  language?: string | null,
): string => {
  if (!recipient) return '';
  if (field === 'currentCountry') {
    return countryName(recipient.currentCountry as string | null, language || 'en');
  }
  if (field === 'budgetWithCurrency') {
    return formatBudget(recipient.budget as string | null, recipient.budgetCurrency as string | null);
  }
  const value = recipient[field];
  return value == null ? '' : String(value);
};
